
import React, { useState, useEffect, lazy, Suspense, useMemo } from 'react';
import { Progress } from '@/components/ui/progress';
import { useFirebase } from '@/context/FirebaseProvider';
import { useToast } from '@/hooks/use-toast';
import { useGoCampaign } from '../../../../../context/GoCampaignContext'; // Import useGoCampaign

// Fix the lazy loading imports to properly type the components
const CheckoutProvider = lazy(() =>
  import('@/context/checkout/CheckoutContext').then(module => ({
    default: module.CheckoutProvider
  }))
);

// Use a proper typing for the lazy-loaded component
const CheckoutSection = lazy(() => 
  import('@/components/business/onboarding/checkout/CheckoutSection').then(module => ({
    default: module.default
  }))
);

interface CheckoutSidebarProps {
  businessId: string;
  onComplete: () => void;
}

/**
 * Sidebar component that displays the checkout summary
 */
const CheckoutSidebar: React.FC<CheckoutSidebarProps> = ({
  businessId,
  onComplete
}) => {
  // const { businesses: allBusinesses } = useFirebase(); // No longer needed directly
  // const business = useMemo(() => 
  //   (allBusinesses || []).find(b => b.id === businessId),
  //   [allBusinesses, businessId]
  // );
  // const { toast } = useToast(); // Keep toast if used elsewhere, otherwise can be removed if not.
  
  const { completedSections, SECTION_ORDER } = useGoCampaign();

  const progressData = useMemo(() => {
    let completedCount = 0;
    if (completedSections) {
      const values = Object.values(completedSections);
      const filteredValues = values.filter(isComplete => isComplete === true);
      completedCount = filteredValues.length;
      // Detailed log for count debugging
      console.log('[CheckoutSidebar] Count Debug: SECTION_ORDER:', SECTION_ORDER);
      console.log('[CheckoutSidebar] Count Debug: completedSections from context:', JSON.stringify(completedSections));
      console.log('[CheckoutSidebar] Count Debug: Object.values(completedSections):', JSON.stringify(values));
      console.log('[CheckoutSidebar] Count Debug: Filtered true values:', JSON.stringify(filteredValues));
      console.log('[CheckoutSidebar] Count Debug: Calculated completedCount:', completedCount);
    }
    const totalSections = SECTION_ORDER?.length || 5; // Use length from context or default
    const progressPercentage = totalSections > 0 ? (completedCount / totalSections) * 100 : 0;
    
    // Original summary log
    console.log('[CheckoutSidebar] Progress calculated (summary):', { completedCount, progressPercentage });
    
    return {
      completedCount,
      progressPercentage
    };
  }, [completedSections, SECTION_ORDER]);
  
  // Determine if all sections are complete based on progressData
  const allSectionsComplete = progressData.completedCount === (SECTION_ORDER?.length || 5);
  
  // Checkout is shown only when all sections are complete
  const showCheckout = allSectionsComplete;
  
  return (
    <div className="w-full lg:w-1/3 h-fit sticky top-24">
      {showCheckout ? (
        <div className="bg-white rounded-lg shadow-sm">
          <Suspense fallback={
            <div className="p-6 flex items-center justify-center">
              <div className="animate-pulse text-center">
                <p className="text-sm text-gray-500">Loading checkout...</p>
              </div>
            </div>
          }>
            <CheckoutProvider businessId={businessId}>
              <CheckoutSection businessId={businessId} onComplete={onComplete} />
            </CheckoutProvider>
          </Suspense>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="w-full space-y-4 text-center">
            <h3 className="font-medium text-lg">Complete all sections to checkout</h3>
            <Progress value={progressData.progressPercentage} className="h-2 w-full" />
            <p className="text-sm text-gray-500">
              {progressData.completedCount} of 5 sections complete
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default CheckoutSidebar;
